import React, { useState, useEffect, memo } from 'react';
import { GeoJsonDataSource, Cesium3DTileset } from "resium";
import * as Cesium from "cesium"; // Import all of Cesium as a namespace
import {
  Color,
  ColorMaterialProperty,
  defined,
  Cesium3DTileStyle,
  PolylineGlowMaterialProperty, // Add for glowing effect
  PolylineArrowMaterialProperty, // Add for optional arrow effect
  HeightReference, // Add import for HeightReference
  Resource, // Add Resource import for authenticated requests
} from "cesium";
import { useCesiumViewer } from '../../../context/CesiumViewerContext'; // Corrected path
import Flood_IoT_sensor from './Flood_IoT_sensor'; // Correct path within layers/
import useCesiumAuth from '../../../hooks/useCesiumAuth'; // Import Cesium authentication hook
import tokenService from '../../../services/auth/tokenService'; // Import token service
// End of Cesium imports
const LayerControl = ({
  // viewerRef prop removed, will get from context
  // Visibility states controlled by FilterManager/MapViewPage
  showRoadLines,
  showIoTSensors,
  show3DModel,
  selectedTilesetAsset, // Add prop for selected asset with 3D model
  selected3dModelVersion,
  // Handlers passed down from MapViewPage
  handleSensorSelect,
  handleSensorUpdate,
  onTilesetLoadError, // Callback for tileset loading errors
  onTilesetReady, // New prop for tileset ready callback
  // BIM model (GIS data) props
  activeBimLayers = [], // Active BIM layers: [{ id, organizationId, assetId, name, flat }]
  onBimTilesetReady, // (layer, tileset) => void
  onBimTilesetError, // (layer, error) => void
  // Dynamic organization layers props
  organizationLayers = [], // Array of organization-specific GeoJSON layers
  loadingOrganizationLayers = false, // Loading state for organization layers
}) => {
  // Get viewerRef from context
  const viewerRef = useCesiumViewer();
  
  // Configure Cesium authentication headers
  useCesiumAuth();

  // JSX for rendering the layers based on visibility props
  return (
    <>
            {/* Load and display organization-specific GeoJSON layers */}
      {showRoadLines && organizationLayers.length > 0 && organizationLayers.map((layer) => {
        // Create authenticated resource for Cesium
        const token = tokenService.getAccessToken();
        const geoJsonUrl = `/api/geojson-proxy/${layer.id}`;
        
        // Create Cesium Resource with authentication headers
        const authenticatedResource = new Resource({
          url: geoJsonUrl,
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        return (
          <GeoJsonDataSource
            key={layer.id}
            data={authenticatedResource} // Use authenticated resource instead of plain URL
            name={layer.layer_name} // Use dynamic layer name
            onLoad={dataSource => {
              // Apply styling after data source is loaded
              dataSource.entities.values.forEach(entity => {
                if (entity.polyline) {
                  // Create dynamic pulsing glow effect
                  const glowMaterial = new PolylineGlowMaterialProperty({
                    glowPower: 0.15, // Less trail-off at the edges
                    taperPower: 0.5, // Fade away glow with distance
                    color: Color.fromCssColorString('#FFC107').withAlpha(0.9), // Amber color with high opacity
                  });
                  
                  // Apply the materials and styling
                  entity.polyline.material = glowMaterial;
                  entity.polyline.width = 7; // Thicker line for more visible effect
                  
                  // IMPORTANT: Clamp polylines to terrain surface
                  entity.polyline.heightReference = HeightReference.CLAMP_TO_GROUND;
                  entity.polyline.clampToGround = true; // Keep road lines clamped to terrain
                }
              });
            }}
            onError={(error) => {
              console.error(`LayerControl: Error loading organization layer ${layer.layer_name}:`, error);
            }}
          />
        );
      })}

      {/* Render the temporary IoT sensor with onSelect and onUpdate handlers */}
      {showIoTSensors && (
        <Flood_IoT_sensor
          onSelect={handleSensorSelect} // Pass down handler
          onUpdate={handleSensorUpdate} // Pass down handler
        />
      )}

      {/* Render 3D Tileset declaratively */}
      {show3DModel && selectedTilesetAsset && selected3dModelVersion && (() => {
        // Create authenticated resource for Cesium
        const token = tokenService.getAccessToken();
        const tilesetUrl = `${process.env.REACT_APP_API_URL}/3d-model-proxy/${selectedTilesetAsset.organization_id || selectedTilesetAsset.organizationId}/${selectedTilesetAsset.id || selectedTilesetAsset.assetId}/${selected3dModelVersion}/tileset.json`;
        
        // Create Cesium Resource with authentication headers
        const authenticatedResource = new Cesium.Resource({
          url: tilesetUrl,
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        return (
          <Cesium3DTileset
            key={selected3dModelVersion} // Add key to force re-render on version change
            // Use authenticated resource instead of plain URL
            url={authenticatedResource}
          onReady={(tileset) => {
            // Call the callback with the loaded tileset
            if (onTilesetReady) {
              onTilesetReady(tileset);
            }
          }}
          onError={(error) => {
            const organizationId = selectedTilesetAsset.organization_id || selectedTilesetAsset.organizationId;
            const assetId = selectedTilesetAsset.id || selectedTilesetAsset.assetId;
            const constructedURL = `${process.env.REACT_APP_API_URL}/3d-model-proxy/${organizationId}/${assetId}/11122024/tileset.json`;
            console.error(`❌ Error loading tileset for asset ${selectedTilesetAsset.id}:`, error);
            console.error(`🔗 Attempted URL: ${constructedURL}`);
            console.error(`📋 Asset object:`, selectedTilesetAsset);
            console.error(`🏢 Organization ID: ${organizationId}, 🎯 Asset ID: ${assetId}`);
            
            // Check if it's an authentication error
            if (error.statusCode === 401 || error.statusCode === 403) {
              console.error(`🚨 Authentication error: User may not have access to this 3D model`);
              if (onTilesetLoadError) {
                onTilesetLoadError(`Access denied: You don't have permission to view the 3D model for asset ${selectedTilesetAsset.id}. Please contact your administrator.`);
              }
            } else {
              // Generic error handling
              if (onTilesetLoadError) {
                onTilesetLoadError(`Failed to load 3D model for asset ${selectedTilesetAsset.id}. The model might not exist or there may be a network issue. URL: ${constructedURL}`);
              }
            }
          }}
          // Apply default style if needed (example)
          // style={new Cesium3DTileStyle({
          //   color: 'Boolean(${feature.height} > 100) ? color("red") : color("blue")'
          // })}
        />
        );
      })()}

      {/* Render every active BIM model layer as its own 3D tileset. BIM models are
          un-dated (single version); flat models live directly under the asset while
          multi-discipline models add one path segment per layer. */}
      {activeBimLayers.map((layer) => {
        const token = tokenService.getAccessToken();
        const seg = layer.flat ? '' : `${encodeURIComponent(layer.name)}/`;
        const bimTilesetUrl = `${process.env.REACT_APP_API_URL}/bim-model-proxy/${layer.organizationId}/${layer.assetId}/${seg}tileset.json`;

        const authenticatedResource = new Cesium.Resource({
          url: bimTilesetUrl,
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        return (
          <Cesium3DTileset
            key={layer.id}
            url={authenticatedResource}
            onReady={(tileset) => {
              if (onBimTilesetReady) {
                onBimTilesetReady(layer, tileset);
              }
            }}
            onError={(error) => {
              if (onBimTilesetError) {
                onBimTilesetError(layer, error);
              }
            }}
          />
        );
      })}

    </>
  );
};

export default memo(LayerControl);